import React, { useEffect, useMemo, useState } from "react";

import { LinearProgress } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useAppState } from "Context/StateContext";
import { Company } from "Models/company";
import { useTranslation } from "react-i18next";

import UserCompanyService from "../../../services/user.company.service";
import { Status } from "../../../util/enums";
import { CompanyNameAndIconContainer } from "./CompanyNameAndIconContainer";
import SearchFilterCompany from "./SearchFilterCompany";
import { FilterOption } from "./SearchFilterCompany/FilterCompany";
import useStyles, {
  CompanyMenuListContainer,
  Divider,
  GridItemContainer,
  MenuOptions,
  SwitchCompanyOption,
  UserProfileSectionTitle,
} from "./styles";

interface UserBlockCompanyMenuProps {
  handleCloseCompanyMenu: () => void;
  handleSwitchCompany: (company: Company) => void;
  userCompanyMenu?: HTMLElement | null;
  loadingSwitchCompany: boolean;
}
export const UserBlockCompanyMenu = ({
  handleCloseCompanyMenu,
  handleSwitchCompany,
  userCompanyMenu,
  loadingSwitchCompany,
}: UserBlockCompanyMenuProps) => {
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();
  const [searchInput, setSearchInput] = useState<string>("");
  const [filterOptions, setFilterOptions] = useState<FilterOption[]>([]);

  const { data: companies, isFetching } = useQuery({
    queryKey: ["user.company.list", currentUser?.id, currentUser?.companyId],
    queryFn: () =>
      UserCompanyService.getAll({
        userId: currentUser?.id,
        status: Status.ACTIVE,
        include: JSON.stringify([
          {
            association: "company",
            attributes: ["id", "name"],
            include: [
              { association: "icon" },
              { association: "profile", attributes: ["id", "name"] },
            ],
            where: { status: Status.ACTIVE },
          },
        ]),
        sortBy: JSON.stringify([["company", "name", "asc"]]),
      }).then(({ data }) => data.map((userCompany) => userCompany.company)),
    initialData() {
      return [] as Company[];
    },
    refetchOnWindowFocus: false,
    enabled: !!currentUser?.id,
    onSuccess: (response) => {
      return response;
    },
  });

  useEffect(() => {
    const profileCompanies = new Map<number | undefined, string>();

    companies.forEach((company) =>
      profileCompanies.set(company.profile?.id, company.profile?.name)
    );

    setFilterOptions(
      Array.from(profileCompanies).map((profile) => ({
        value: profile[1],
        key: profile[0],
        checked: false,
      }))
    );
  }, [companies]);

  const hasCompany = useMemo(() => {
    return !!companies.length;
  }, [companies]);

  const filteredCompanies = useMemo(() => {
    const filterSelectedKeys = filterOptions
      .filter((filterOption) => filterOption.checked === true)
      .map((filterOption) => filterOption.key);

    const valuesWithFilterApplied = filterSelectedKeys?.length
      ? companies.filter((company) => filterSelectedKeys?.includes(company?.profile?.id))
      : companies;

    return valuesWithFilterApplied?.filter(
      (company) =>
        company.id !== currentUser?.companyId &&
        (searchInput?.trim()
          ? company?.name?.toLowerCase()?.includes(searchInput?.toLowerCase())
          : true)
    );
  }, [searchInput, filterOptions, currentUser?.id, companies]);

  const renderCompanySection = () => {
    if (isFetching)
      return <LinearProgress sx={{ height: "4px", borderRadius: "8px", marginTop: "8px" }} />;

    if (!hasCompany) return;

    return (
      <>
        <Divider />

        <UserProfileSectionTitle>{translate("changeTo")}</UserProfileSectionTitle>

        <SearchFilterCompany
          onChangeInput={setSearchInput}
          searchInput={searchInput}
          setFilterOptions={setFilterOptions}
          filterOptions={filterOptions}
        />

        <CompanyMenuListContainer scroll={filteredCompanies.length > 5}>
          {filteredCompanies.map((company, index) => {
            return (
              <SwitchCompanyOption
                key={company.id}
                disabled={loadingSwitchCompany}
                data-cy={`companySwitchOption${index}`}
                onClick={() => {
                  handleSwitchCompany(company);
                }}
              >
                <CompanyNameAndIconContainer company={company} showProfileName={true} />
              </SwitchCompanyOption>
            );
          })}
        </CompanyMenuListContainer>
      </>
    );
  };

  return (
    <MenuOptions
      anchorEl={userCompanyMenu}
      keepMounted={false}
      open={Boolean(userCompanyMenu)}
      onClose={handleCloseCompanyMenu}
      anchorOrigin={{ vertical: "top", horizontal: "left" }}
      transformOrigin={{ horizontal: "right", vertical: "top" }}
      PaperProps={{
        className: classes.menuOptionsPaperProps,
      }}
    >
      <GridItemContainer container direction="column">
        <UserProfileSectionTitle>{translate("currentlyViewing")}</UserProfileSectionTitle>

        {currentUser && (
          <CompanyNameAndIconContainer company={currentUser?.company} showProfileName={true} />
        )}

        {renderCompanySection()}
      </GridItemContainer>
    </MenuOptions>
  );
};
